import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import {
  intlShape,
  injectIntl,
  FormattedMessage,
  FormattedNumber
} from 'react-intl';
import * as Util from '../../../common/utilities/';
import * as StringUtils from '../../../utilities/stringUtils';
import { withFlags } from '../../../flags/Flags';

import {
  Accordion,
  AccordionPanel,
  Checkbox,
  Combobox,
  Input,
  Icon,
  RadioButtonGroup,
  Radio
} from '@salesforce/design-system-react';
import { EmptyChest } from '../images/illustrations/no-data';

import './BlockConfiguration.scss';
import { SpotlightConfiguration } from '../index';

class BlockConfiguration extends Component {
  constructor(props) {
    super(props);
    this.anchors = {
      subjectLine: React.createRef(),
      spotlight: React.createRef(),
      warning: React.createRef()
    };

    this.state = {
      // we only want to show reminder to remove ampscript after user has unchecked the subject line box
      didUncheck: false,
      expandedPanels: {},
      copied: {
        subject_line: false,
        preheader: false
      },
      alignment: this.props.forms?.selection?.alignment || 'left'
    };
  }

  componentDidUpdate(prevProps, prevState) {
    // Update super content once a random asset is loaded
    if (Util.propChanged(prevProps, this.props, 'randomAsset')) {
      if (this.props.randomAsset?.image_link) {
        const element = this.superContentElement(
          this.props.forms.selection.assetType.selection[0].label,
          this.props.randomAsset.image_link
        );

        this.props.actions.setSuperContentInSdk(element);
      }
    }

    if (Util.propChanged(prevProps, this.props, 'forms.selection.spotlight')) {
      const keys = (this.props.forms.selection.spotlight || []).map(s => s.key);
      keys
        .filter(key => !Object.keys(this.props.attributeValues).includes(key))
        .forEach(key => this.props.actions.getAssetAttributeValues(key));
    }

    if (
      Util.propChanged(
        prevProps,
        this.props,
        'forms.selection.subjectLine',
        true
      )
    ) {
      if (this.anchors.subjectLine?.current) {
        this.anchors.subjectLine.current.scrollIntoView({ behavior: 'smooth' });
      }
    }

    if (
      Util.propChanged(
        prevProps,
        this.props,
        'forms.selection.spotlightDistribution'
      )
    ) {
      if (this.hasCompleteSpotlight()) {
        if (this.anchors.warning?.current) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      }
    }

    ['subjectLine', 'spotlight'].forEach(section => {
      if (
        Util.propChanged(
          prevState,
          this.state,
          `expandedPanels.${section}`,
          true
        )
      ) {
        const ref = this.anchors[section];
        if (ref?.current) {
          ref.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }

  getI18nID(suffix) {
    return `components.ecs.blocks.configuration.${suffix}`;
  }

  handleAssetTypeChange(evt, data) {
    const inputValue = data.selection[0]?.value;

    let updates = {
      assetType: { selection: data.selection, inputValue }
    };

    if (!data.selection[0]?.canControlSubjectLine) {
      updates.subjectLine = false;
    }

    this.props.formChange('selection', updates);
  }

  currentSelection() {
    const initSelection = { selection: [], inputValue: '' };
    const selection = this.props.forms?.selection;
    return (selection && selection.assetType) || initSelection;
  }

  selectedAssetType() {
    return this.currentSelection().selection?.[0]?.id;
  }

  canControlSubjectLine() {
    return (
      this.currentSelection().selection?.[0]?.canControlSubjectLine || false
    );
  }

  selectionFor(assetType) {
    return {
      id: assetType.type,
      label: assetType.type,
      count: assetType.count,
      canControlSubjectLine: assetType.control_subject_line
    };
  }

  // Accepts a custom menu item rendering function that becomes a custom component.
  // It should return a React node.
  menuItem(props) {
    const { formatMessage } = this.props.intl;
    const count = formatMessage(
      { id: 'components.ecs.assets.countByType' },
      { count: props.option.count }
    );
    return (
      <div>
        <p className="combobox-left">{props.option.label}</p>
        <p className="combobox-right">{count}</p>
        <div className="combobox-div-clear" />
      </div>
    );
  }

  superContentElement(assetType, superImageUrl) {
    const { formatMessage } = this.props.intl;
    const info = formatMessage({
      id: 'components.ecs.blocks.superContentInfo'
    });

    // Stylesheets and React components will not work inside super content, only raw html so we have to use
    // the style prop directly and ReactDOMServer.renderToStaticMarkup()
    const style = {
      left: '50%',
      position: 'absolute',
      backgroundColor: 'rgb(80,80,80, 0.6)',
      width: '55%',
      marginLeft: '-27%',
      display: 'flex',
      alignContent: 'center',
      alignItems: 'center',
      padding: '6px 10px 6px 10px',
      color: '#eeeeee',
      fontSize: '0.8rem',
      borderRadius: '2px'
    };

    const superContentBlock = (
      <div style={{ position: 'relative' }}>
        <div style={style}>
          <div style={{ marginRight: '4px' }}>
            <svg
              aria-hidden="true"
              style={{
                fill: '#eeeeee',
                width: '1.5rem',
                height: '1.5rem',
                lineHeight: '1'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                id="preview"
              >
                <path d="M23.9 11.6C21.7 7.2 17.2 4.2 12 4.2S2.3 7.2.1 11.6c-.1.3-.1.6 0 .8 2.2 4.4 6.7 7.4 11.9 7.4s9.7-3 11.9-7.4c.1-.3.1-.5 0-.8zM12 17.1c-2.8 0-5.1-2.3-5.1-5.1S9.2 6.9 12 6.9s5.1 2.3 5.1 5.1-2.3 5.1-5.1 5.1zm0-8.3c-1.8 0-3.2 1.4-3.2 3.2s1.4 3.2 3.2 3.2 3.2-1.4 3.2-3.2-1.4-3.2-3.2-3.2z" />
              </svg>
            </svg>
          </div>
          {info}
        </div>
        <img
          alt="Sample Content"
          src={superImageUrl}
          width="100%"
          height="auto"
        />
      </div>
    );
    return ReactDOMServer.renderToStaticMarkup(superContentBlock);
  }

  distributionNode() {
    if (this.spotlight().length) {
      return this.spotlightDistribution();
    }

    const assetType = this.selectedAssetType();

    let distributionKey = 'type';
    let distributionValue = assetType;

    if (distributionKey && distributionValue) {
      const distribution = this.props.distribution?.[distributionKey]?.[
        distributionValue
      ];
      return (
        distribution || { active: 0, expiring_7_days: 0, expiring_30_days: 0 }
      );
    }
    return null;
  }

  hasCompleteSpotlight() {
    if (this.props.forms.selection?.spotlight) {
      // Old style
      if (this.props.forms.selection.spotlight.asset_value) {
        return true;
      }

      // New style
      return Object.entries(this.props.forms.selection.spotlight).every(
        spotlight => !!spotlight[1].key && !!spotlight[1].value
      );
    }
    return false;
  }

  renderDistribution() {
    const assetType = this.selectedAssetType();
    const distribution = this.distributionNode();

    if (!assetType || !distribution) {
      return null;
    }

    const { active, expiring_7_days, expiring_30_days } = distribution;

    let warning;
    if (!active) {
      let warningType = 'noAssetsWarning';
      if (this.hasCompleteSpotlight()) {
        warningType = 'noAssetsSpotlightWarning';
      }
      warning = (
        <div
          className="rounded-card configuration-container configuration-alert"
          ref={this.anchors.warning}
        >
          <div className="subject-line-warning">
            <div style={{ color: 'black' }}>
              <div className="slds-current-color centered-icon">
                <Icon
                  assistiveText={{ label: 'Warning' }}
                  category="utility"
                  name="warning"
                  size="small"
                />
              </div>
            </div>
            <FormattedMessage
              id={this.getI18nID(`distribution.${warningType}`)}
            />
          </div>
        </div>
      );
    }

    return (
      <React.Fragment>
        {warning}
        <div className="rounded-card configuration-container">
          <FormattedMessage id={this.getI18nID('distribution.title')} />
          <div className="distribution">
            <div className="active">
              <FormattedMessage
                id={this.getI18nID('distribution.activeAssets')}
              />
              <FormattedNumber value={active} />
            </div>
            <div className="expiring_7_days">
              <FormattedMessage
                id={this.getI18nID('distribution.expiring_7_days')}
              />
              <FormattedNumber value={expiring_7_days} />
            </div>
            <div className="expiring_30_days">
              <FormattedMessage
                id={this.getI18nID('distribution.expiring_30_days')}
              />
              <FormattedNumber value={expiring_30_days} />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderNoFallback() {
    return (
      <div className="rounded-card no-fallback">
        <div className="illustration-container">
          <EmptyChest />
        </div>
        <div>
          <FormattedMessage id={this.getI18nID('noFallback.title')} />
          <FormattedMessage id={this.getI18nID('noFallback.info')} />
        </div>
      </div>
    );
  }

  renderFallback() {
    const assetType = this.selectedAssetType();
    const typesWithFallback =
      this.props.assetTypes.length && this.props.assetTypes;
    if (!assetType || !typesWithFallback) {
      return null;
    }

    const typeData = typesWithFallback.find(
      typeObj => typeObj.type === assetType
    );
    if (!typeData || !typeData.fallback) {
      return this.renderNoFallback();
    }

    return (
      <div className="rounded-card configuration-container">
        <FormattedMessage id={this.getI18nID('fallback.title')} />
        <div className="fallback">
          <img
            alt="fallback thumbnail"
            width="75px"
            height="75px"
            src={typeData.fallback_image_link}
          />
          <FormattedMessage id={this.getI18nID('fallback.info')} />
        </div>
      </div>
    );
  }

  /**
   * Returns React node with instructions on what ampscript to copy into the subject line/preheader
   *
   * Note: there is a 400 character limit on the subject line field inside Marketing Cloud.
   *
   * @param field - string of either "subject_line" or "preheader"
   */
  renderAMPScriptStep(field) {
    const preheader = '%%=v(@ECS_PREHEADER)=%%';
    const subjectLine = '%%=v(@ECS_SUBJECT_LINE)=%%';

    const script = field === 'subject_line' ? subjectLine : preheader;
    return (
      <React.Fragment>
        <FormattedMessage
          id={this.getI18nID(`subjectLine.instructions.${field}`)}
        />
        <div className="subject-line-details ampscript">
          <pre>{script}</pre>
        </div>
      </React.Fragment>
    );
  }

  spotlight() {
    const spotlight = this.props.forms?.selection?.spotlight;
    if (spotlight) {
      if (Array.isArray(spotlight)) {
        return spotlight;
      } else {
        return [{ [spotlight.asset_key]: spotlight.asset_value }];
      }
    }
    return [];
  }

  spotlightDistribution() {
    return this.props.forms?.selection?.spotlightDistribution;
  }

  height() {
    const value = this.props.forms?.selection?.height;
    return this.dimensionValue(value);
  }

  width() {
    const value = this.props.forms?.selection?.width;
    return this.dimensionValue(value);
  }

  maxWidth() {
    const value = this.props.forms?.selection?.maxWidth;
    return this.maxWidthValue(value);
  }

  altText() {
    const value = this.props.forms?.selection?.altText;
    return this.altTextValue(value);
  }

  dimensionValue(value) {
    return value === undefined ||
      value.length === 0 ||
      value === '0' ||
      value <= 0
      ? 'auto'
      : value;
  }

  maxWidthValue(value) {
    return value === undefined ||
      value.length === 0 ||
      !(
        value.match(/^\d+(px|em|rem|%)?$/) ||
        value === 'none' ||
        value === 'initial' ||
        value === 'inherit'
      )
      ? '100%'
      : value;
  }

  altTextValue(value) {
    return value === undefined || value.length === 0 ? '' : value;
  }

  handleDisplayHeightChange(value) {
    value = this.dimensionValue(value);
    this.displayHeight.value = value;

    const updates = { height: value };
    this.props.formChange('selection', updates);
  }

  handleDisplayWidthChange(value) {
    value = this.dimensionValue(value);
    this.displayWidth.value = value;

    const updates = { width: value };
    this.props.formChange('selection', updates);
  }

  handleDisplayMaxWidthChange(value) {
    value = this.maxWidthValue(value);
    this.displayMaxWidth.value = value;

    const updates = { maxWidth: value };
    this.props.formChange('selection', updates);
  }

  handleDisplayAltTextChange(value) {
    value = this.altTextValue(value);
    this.displayAltText.value = value;

    const updates = { altText: value };
    this.props.formChange('selection', updates);
  }

  renderBlockSize() {
    const { formatMessage } = this.props.intl;

    const labels = {
      height: formatMessage({
        id: this.getI18nID('blockSize.height')
      }),
      width: formatMessage({
        id: this.getI18nID('blockSize.width')
      }),
      maxWidth: formatMessage({
        id: this.getI18nID('blockSize.maxWidth')
      }),
      placeholder: formatMessage({
        id: this.getI18nID('blockSize.auto')
      }),
      altText: formatMessage({
        id: this.getI18nID('blockSize.altText')
      })
    };

    return (
      <div className="configuration-container" ref={this.anchors.blocksize}>
        <div className="subject-line-info">
          <FormattedMessage id={this.getI18nID('blockSize.label')} />
        </div>
        <div className="slds-col attribute-cell">
          <Input
            type="number"
            label={labels.height}
            placeholder={labels.placeholder}
            onBlur={e => this.handleDisplayHeightChange(e.target.value)}
            defaultValue={this.height()}
            inputRef={el => (this.displayHeight = el)}
          />
        </div>
        <div className="slds-col attribute-cell">
          <Input
            type="number"
            label={labels.width}
            placeholder={labels.placeholder}
            onBlur={e => this.handleDisplayWidthChange(e.target.value)}
            defaultValue={this.width()}
            inputRef={el => (this.displayWidth = el)}
          />
        </div>
        <div className="slds-col attribute-cell">
          <Input
            type="text"
            label={labels.maxWidth}
            placeholder="100%"
            onBlur={e => this.handleDisplayMaxWidthChange(e.target.value)}
            defaultValue={this.maxWidth()}
            inputRef={el => (this.displayMaxWidth = el)}
          />
        </div>
        <div className="slds-col attribute-cell">
          <RadioButtonGroup
            className="align-radio"
            labels={{
              label: formatMessage({
                id: this.getI18nID('align.label')
              })
            }}
            onChange={event => {
              this.setState({ alignment: event.target.value });
              this.props.formChange('selection', {
                alignment: event.target.value
              });
            }}
          >
            <Radio
              key="left"
              id="left"
              labels={{
                label: formatMessage({
                  id: this.getI18nID('align.left')
                })
              }}
              value="left"
              checked={this.state.alignment === 'left'}
              variant="button-group"
            />
            <Radio
              key="center"
              id="center"
              labels={{
                label: formatMessage({
                  id: this.getI18nID('align.center')
                })
              }}
              value="center"
              checked={this.state.alignment === 'center'}
              variant="button-group"
            />
            <Radio
              key="right"
              id="right"
              labels={{
                label: formatMessage({
                  id: this.getI18nID('align.right')
                })
              }}
              value="right"
              checked={this.state.alignment === 'right'}
              variant="button-group"
            />
          </RadioButtonGroup>
        </div>
        <div className="slds-col attribute-cell">
          <Input
            type="text"
            label={labels.altText}
            placeholder=""
            onBlur={e => this.handleDisplayAltTextChange(e.target.value)}
            defaultValue={this.altText()}
            inputRef={el => (this.displayAltText = el)}
          />
        </div>
      </div>
    );
  }

  renderSpotlight() {
    if (!this.props.forms?.selection?.assetType?.selection?.length) {
      return null;
    }

    return (
      <div className="configuration-container" ref={this.anchors.spotlight}>
        <SpotlightConfiguration
          spotlight={this.spotlight()}
          attributes={this.props.attributes}
          attributeValues={this.props.attributeValues}
          onSpotlightChange={spotlight =>
            this.props.formChange('selection', { spotlight })
          }
        />
      </div>
    );
  }

  renderSubjectLine(flags) {
    if (!flags.subjectLine || !this.canControlSubjectLine()) {
      return null;
    }

    const { formatMessage } = this.props.intl;
    const label = formatMessage({
      id: this.getI18nID('subjectLine.label')
    });

    const controlsSubjectLine =
      this.props.forms.selection?.subjectLine || false;
    let details;
    if (controlsSubjectLine) {
      details = (
        <div className="subject-line-details">
          {this.renderAMPScriptStep('subject_line')}
          {this.renderAMPScriptStep('preheader')}
        </div>
      );
    } else if (this.state.didUncheck) {
      details = (
        <div className="subject-line-details subject-line-warning">
          <Icon
            assistiveText={{ label: 'Warning' }}
            category="utility"
            colorVariant="warning"
            name="warning"
            size="small"
          />
          <FormattedMessage
            id={this.getI18nID('subjectLine.instructions.remove')}
          />
        </div>
      );
    }

    return (
      <div className="configuration-container" ref={this.anchors.subjectLine}>
        <div className="subject-line-info">
          <FormattedMessage id={this.getI18nID('subjectLine.details')} />
        </div>
        <Checkbox
          className="subject-line-checkbox"
          labels={{ label }}
          value={controlsSubjectLine}
          checked={controlsSubjectLine}
          onChange={(evt, { checked }) => {
            if (!checked) {
              this.setState({
                didUncheck: true
              });
            }
            this.props.formChange('selection', {
              subjectLine: checked
            });
          }}
        />
        {details}
      </div>
    );
  }

  togglePanel(section) {
    const expandedPanels = { ...this.state.expandedPanels };
    expandedPanels[section] = !this.state.expandedPanels[section];
    this.setState({ expandedPanels });
  }

  renderAdvanced() {
    const { formatMessage } = this.props.intl;

    return withFlags(flags => {
      const advanced = {
        subjectLine: this.renderSubjectLine(flags),
        spotlight: this.renderSpotlight(),
        blockSize: this.renderBlockSize(flags)
      };

      return (
        <React.Fragment>
          <header className="configuration-header">
            <FormattedMessage id={this.getI18nID('advanced')} />
          </header>
          <Accordion>
            {Object.entries(advanced)
              .filter(entry => !!entry[1])
              .map(entry => {
                const section = entry[0];
                const content = entry[1];
                const summary = formatMessage({
                  id: this.getI18nID(`${section}.title`)
                });
                return (
                  <AccordionPanel
                    expanded={!!this.state.expandedPanels[section]}
                    id={section}
                    key={`accordion-${section}`}
                    onTogglePanel={() => this.togglePanel(section)}
                    summary={summary}
                  >
                    {content}
                  </AccordionPanel>
                );
              })}
          </Accordion>
        </React.Fragment>
      );
    });
  }

  assetTypeOptions() {
    const isClassPresent = uuid =>
      (this.props.messageBlocks || [])
        .map(block => block.asset_class_id)
        .includes(uuid);

    const isSelectable = assetClass =>
      !assetClass.parent_class_id || isClassPresent(assetClass.parent_class_id);

    const types = this.props.assetTypes || [];

    return types
      .sort(StringUtils.sortByField('name', this.props.intl.locale))
      .filter(isSelectable)
      .map(this.selectionFor.bind(this));
  }

  renderWeatherWarning() {
    if (!this.props.isValidWeather) {
      const { formatMessage } = this.props.intl;
      const helpLink =
        'https://help.salesforce.com/articleView?id=mc_pb_einstein_content_selection.htm&type=5';
      return (
        <div className="rounded-card configuration-container configuration-alert">
          <div className="subject-line-warning">
            <div style={{ color: 'black' }}>
              <div className="slds-current-color centered-icon">
                <Icon
                  assistiveText={{ label: 'Warning' }}
                  category="utility"
                  name="warning"
                  size="small"
                />
              </div>
            </div>
            <FormattedMessage
              id={this.getI18nID('missingWeatherMap')}
              values={{
                helpLink: (
                  <a rel="noopener noreferrer" target="_blank" href={helpLink}>
                    {formatMessage({
                      id: 'components.ecs.nocatalog.helpLinkText'
                    })}
                  </a>
                )
              }}
            />
          </div>
        </div>
      );
    }
  }

  render() {
    const { formatMessage } = this.props.intl;

    const assetTypeStrings = {
      title: formatMessage({
        id: this.getI18nID('assetType.title')
      })
    };

    const disabled = this.props.forms?.selection?.isExistingBlock;
    const selection = this.currentSelection();
    return (
      <div>
        <div className="asset-type">
          <header className="configuration-header">
            <span className="required">*</span>
            {assetTypeStrings.title}
          </header>
          <div className="configuration-container slds-radio">
            <FormattedMessage id={this.getI18nID('assetType.label')} />
            <Combobox
              classNameContainer="asset-type-combobox"
              readonly
              singleInputDisabled={disabled}
              onRenderMenuItem={this.menuItem.bind(this)}
              options={this.assetTypeOptions()}
              selection={selection.selection}
              value={selection.inputValue}
              events={{
                onSelect: this.handleAssetTypeChange.bind(this)
              }}
              variant="readonly"
            />
          </div>
          {this.renderWeatherWarning()}
          {this.renderDistribution()}
          {this.renderFallback()}
        </div>
        {this.renderAdvanced()}
      </div>
    );
  }
}

BlockConfiguration.propTypes = {
  actions: PropTypes.object,
  assetTypes: PropTypes.array,
  attributes: PropTypes.array,
  attributeValues: PropTypes.object,
  distribution: PropTypes.object,
  forms: PropTypes.object,
  formChange: PropTypes.func,
  isValidWeather: PropTypes.bool,
  page: PropTypes.object,
  messageBlocks: PropTypes.array,
  randomAsset: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(BlockConfiguration);
